
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarSection } from "./CalendarSection";
import { TimeSlotSelector } from "./TimeSlotSelector";
import { DialogFooter } from "./DialogFooter";
import { MentorshipInfoSection } from "./MentorshipInfoSection";
import { isBefore, startOfDay } from "date-fns";
import { useMentorScheduleAvailability, TimeSlot } from "@/hooks/useMentorScheduleAvailability";
import { useScheduleMentorship } from "@/hooks/useScheduleMentorship";
import { ScheduleDialogLoading } from "./ScheduleDialogLoading";
import { ScheduleDialogError } from "./ScheduleDialogError";

export interface ScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mentorId: string;
  offeringId: string;
  gameId?: string;
  offeringTitle: string;
  description?: string;
  coverImageUrl?: string | null;
  isGroup?: boolean;
  maxParticipants?: number;
  gameName?: string;
  price?: number;
}

export const ScheduleDialog: React.FC<ScheduleDialogProps> = ({
  open,
  onOpenChange,
  mentorId,
  offeringId,
  gameId,
  offeringTitle,
  description,
  coverImageUrl,
  isGroup,
  maxParticipants,
  gameName,
  price = 100,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  
  // Get the mentor's availability
  const {
    availableTimeSlots,
    isLoading, // Loading inicial (slots) - modal abre quando false
    isDatesLoading, // Loading das datas disponíveis
    error,
    isDateAvailable
  } = useMentorScheduleAvailability(mentorId, selectedDate);

  // Use our custom hook for scheduling
  const { isProcessing, handleSchedule } = useScheduleMentorship({
    mentorId,
    offeringId,
    gameId,
    offeringTitle: offeringTitle || gameName || "",
    price
  });

  // Disable dates in the past and dates with no availability
  const disabledDays = (date: Date) => {
    // Sempre desabilitar datas passadas
    const isInPast = isBefore(date, startOfDay(new Date()));
    
    // Só verificar disponibilidade se a função estiver definida
    const isUnavailable = typeof isDateAvailable === 'function' ? !isDateAvailable(date) : false;
    
    return isInPast || isUnavailable;
  };

  const handleScheduleClick = async () => {
    if (selectedDate && selectedTimeSlot) {
      console.log("Scheduling with date:", selectedDate, "and time slot:", selectedTimeSlot);
      console.log("Using gameId:", gameId);
      console.log("Using price:", price); // Log the price being used
      
      const success = await handleSchedule(selectedDate, selectedTimeSlot);
      
      if (success) {
        // Will be redirected to Stripe by the hook
        // No need to close dialog
      }
    }
  };

  // Render loading state
  if (isLoading) {
    return <ScheduleDialogLoading open={open} onOpenChange={onOpenChange} />;
  }

  // Render error state
  if (error) {
    return <ScheduleDialogError open={open} onOpenChange={onOpenChange} error={error} />;
  }

  return (
    <Dialog open={open} onOpenChange={(openState) => !openState && onOpenChange(false)} modal>
      <DialogContent 
        className="bg-[#0F141C] border-[#223342] text-white max-w-md overflow-y-auto max-h-[90vh]"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Agendar Mentoria</DialogTitle>
        </DialogHeader>

        <MentorshipInfoSection 
          title={offeringTitle || gameName || "Mentoria"}
          description={description}
          coverImageUrl={coverImageUrl}
          isGroup={isGroup}
          maxParticipants={maxParticipants}
          gameName={gameName}
          price={price}
        />

        <CalendarSection 
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          disabledDays={disabledDays}
          isDatesLoading={isDatesLoading}
        />
        
        {selectedDate && (
          <TimeSlotSelector
            selectedDate={selectedDate}
            timeSlots={availableTimeSlots}
            selectedTimeSlot={selectedTimeSlot}
            onTimeSlotSelect={setSelectedTimeSlot}
          />
        )}

        <DialogFooter
          onClose={() => onOpenChange(false)}
          onSchedule={handleScheduleClick}
          isScheduleDisabled={!selectedTimeSlot || isProcessing}
          isProcessing={isProcessing}
        />
      </DialogContent>
    </Dialog>
  );
};

export * from "./CalendarSection";
export * from "./TimeSlotSelector";
export * from "./DialogFooter";
export * from "./LoadingState";
export * from "./MentorshipInfoSection";
export * from "./ScheduleDialogLoading";
export * from "./ScheduleDialogError";
